import { AKComponent } from '@theme/component';
import { clamp } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * Slideshow variant where slides are stacked like cards.
 * Uses a hidden scroll-snap track to detect swipe direction,
 * while the visual stack (active/next-one/next-two) is rendered as
 * absolutely-positioned overlapping cards.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller - Container for the stacked slides
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [indicators]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {AKComponent<Refs>}
 */
export class SlideshowCards extends AKComponent {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        if (!this.isConnected || !this.refs.slides?.length) return;
        const index = clamp(parseInt(newValue ?? '0', 10) || 0, 0, this.refs.slides.length - 1);
        this.select(index, undefined, { animate: false });
      });
    }
  }

  requiredRefs = ['scroller'];

  /** @type {HTMLElement | null} */
  #scrollTrack = null;

  async connectedCallback() {
    super.connectedCallback();
    const slideCount = this.slides?.length ?? 0;

    if (slideCount <= 1) {
      this.#setupWithoutControls();
    } else {
      this.#setupWithControls();
      this.#createPagination();
      this.#buildScrollTrack();
      this.#setupScrollNavigation();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardownScrollNavigation();
  }

  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;
    return clamp(parseInt(initialSlide, 10) || 0, 0, Math.max(0, (this.refs.slides?.length ?? 1) - 1));
  }

  select(index, event, options = {}) {
    const slides = this.slides;
    if (!slides?.length) return;

    const i = clamp(index, 0, slides.length - 1);
    if (i === this.#current) return;

    event?.preventDefault();

    const previousIndex = this.#current;
    this.current = i;

    this.#scrollTrackTo(i, options.animate !== false);

    const slide = slides[i];
    if (slide) {
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: i,
          previousIndex,
          userInitiated: event != null,
          trigger: 'select',
          slide,
          id: slide.getAttribute('slide-id'),
        })
      );
    }
  }

  next(event, options) {
    event?.preventDefault();
    const slides = this.slides;
    if (!slides?.length) return;
    const nextIndex = this.#current < slides.length - 1 ? this.#current + 1 : 0;
    this.select(nextIndex, event, options);
  }

  previous(event, options) {
    event?.preventDefault();
    const slides = this.slides;
    if (!slides?.length) return;
    const prevIndex = this.#current > 0 ? this.#current - 1 : slides.length - 1;
    this.select(prevIndex, event, options);
  }

  #current = 0;

  get current() {
    return this.#current;
  }

  set current(value) {
    const { slides, previous, next } = this.refs;
    this.#current = value;

    this.#applyStackClasses();

    if (previous) previous.disabled = value === 0;
    if (next && slides?.length) next.disabled = value >= slides.length - 1;

    this.#updatePagination();
  }

  get slides() {
    return (
      this.refs.slides?.filter(
        (slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal')
      ) ?? []
    );
  }

  #applyStackClasses() {
    const slides = this.slides;
    if (!slides?.length) return;
    const cur = this.#current;

    slides.forEach((slide, index) => {
      slide.classList.remove('active', 'next-one', 'next-two', 'behind');
      slide.setAttribute('aria-hidden', index === cur ? 'false' : 'true');

      if (index === cur) slide.classList.add('active');
      else if (index === cur + 1) slide.classList.add('next-one');
      else if (index === cur + 2) slide.classList.add('next-two');
      else slide.classList.add('behind');
    });
  }

  #setupWithoutControls() {
    this.current = this.initialSlideIndex;
    this.#applyStackClasses();
    const activeSlide = this.refs.slides?.[this.#current];
    if (activeSlide) activeSlide.setAttribute('aria-hidden', 'false');
  }

  #setupWithControls() {
    this.current = this.initialSlideIndex;
  }

  #createPagination() {
    const indicators = this.refs.indicators;
    if (!indicators) return;
    const slides = this.slides;
    const count = slides?.length ?? 0;
    if (count <= 1) return;

    const container = Array.isArray(indicators) ? indicators[0] : indicators;
    if (!container) return;

    for (let i = 0; i < count; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = `c-dot${i === this.#current ? ' c-dot--active' : ''}`;
      dot.setAttribute('aria-pressed', i === this.#current ? 'true' : 'false');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.setAttribute('data-index', String(i));
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.select(i, e);
      });
      container.appendChild(dot);
    }
  }

  #updatePagination() {
    const dots = this.querySelectorAll('.c-dot');
    if (!dots.length) return;
    const cur = this.#current;
    dots.forEach((dot, i) => {
      const isActive = i === cur;
      dot.classList.toggle('c-dot--active', isActive);
      dot.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  // ─── Hidden scroll track ────────────────────────────────────────────────────

  #buildScrollTrack() {
    const { scroller } = this.refs;
    if (!scroller) return;

    const count = this.slides.length;

    const track = document.createElement('div');
    track.className = 'c-slideshow-cards__scroll-track';
    track.setAttribute('aria-hidden', 'true');
    track.setAttribute('tabindex', '-1');

    for (let i = 0; i < count; i++) {
      const stop = document.createElement('div');
      stop.className = 'c-slideshow-cards__scroll-stop';
      track.appendChild(stop);
    }

    // Place track as sibling after scroller — CSS will overlay it
    scroller.insertAdjacentElement('afterend', track);
    this.#scrollTrack = track;
  }

  /** @type {AbortController | null} */
  #scrollAbort = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #scrollEndTimer = null;
  #isProgrammaticScroll = false;

  #setupScrollNavigation() {
    const track = this.#scrollTrack;
    if (!track) return;

    this.#scrollAbort = new AbortController();
    const { signal } = this.#scrollAbort;

    track.addEventListener(
      'scroll',
      () => {
        if (this.#isProgrammaticScroll) return;
        if (this.#scrollEndTimer) clearTimeout(this.#scrollEndTimer);
        this.#scrollEndTimer = setTimeout(() => this.#onScrollEnd(), 80);
      },
      { signal, passive: true }
    );
  }

  #teardownScrollNavigation() {
    this.#scrollAbort?.abort();
    this.#scrollAbort = null;
    if (this.#scrollEndTimer) clearTimeout(this.#scrollEndTimer);
  }

  #onScrollEnd() {
    const track = this.#scrollTrack;
    if (!track) return;

    const trackWidth = track.clientWidth;
    if (!trackWidth) return;

    const rawIndex = track.scrollLeft / trackWidth;
    const closestIndex = clamp(Math.round(rawIndex), 0, this.slides.length - 1);

    if (closestIndex === this.#current) return;

    const previousIndex = this.#current;
    this.current = closestIndex;

    const slide = this.slides[closestIndex];
    if (slide) {
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: closestIndex,
          previousIndex,
          userInitiated: true,
          trigger: 'select',
          slide,
          id: slide.getAttribute('slide-id'),
        })
      );
    }
  }

  #scrollTrackTo(index, smooth = true) {
    const track = this.#scrollTrack;
    if (!track) return;

    this.#isProgrammaticScroll = true;

    track.scrollTo({
      left: index * track.clientWidth,
      behavior: smooth ? 'smooth' : 'instant',
    });

    setTimeout(() => {
      this.#isProgrammaticScroll = false;
    }, 500);
  }
}

if (!customElements.get('slideshow-component-cards')) {
  customElements.define('slideshow-component-cards', SlideshowCards);
}